import { ComponentPropsWithoutRef, forwardRef, ReactNode } from 'react';

import { Text14M } from '@sternum/ui';
import { TextOverflowBox } from '@sternum/web/components/TextOverflowBox';
import { Colors } from '@sternum/web/components/theme/constants';

import { Container, Content, Header, Title, TitleWrapper } from './BaseWidget.styles';

export interface BaseWidgetProps extends Omit<ComponentPropsWithoutRef<'div'>, 'title'> {
  title?: ReactNode;
  beforeTitle?: ReactNode;
  actions?: ReactNode;
  noContentPadding?: boolean;
  noBorder?: boolean;
  noOverflow?: boolean;
  background?: string;
}

export const BaseWidget = forwardRef<HTMLDivElement, BaseWidgetProps>(function BaseWidget(
  {
    children,
    title: titleFromProps,
    beforeTitle,
    actions,
    noContentPadding = false,
    noBorder = false,
    noOverflow = false,
    background = Colors.White,
    ...props
  },
  ref,
) {
  const title =
    typeof titleFromProps === 'string' ? (
      <TextOverflowBox
        tooltipContent={titleFromProps}
        displayTooltipOnlyIfNeeded
        tooltipProps={{ position: 'bottom', style: { maxWidth: 300 } }}
      >
        <Text14M>{titleFromProps}</Text14M>
      </TextOverflowBox>
    ) : (
      titleFromProps
    );

  const isHeaderVisible = title || actions;

  return (
    <Container ref={ref} role="presentation" aria-label="base widget container" $background={background} {...props}>
      {isHeaderVisible && (
        <Header role="presentation" aria-label="base widget header" $noBorder={noBorder}>
          <TitleWrapper>
            {beforeTitle}
            <Title>{title}</Title>
          </TitleWrapper>
          {actions}
        </Header>
      )}

      <Content $noPadding={noContentPadding} $noBorder={noBorder} $noOverflow={noOverflow} data-item="content">
        {children}
      </Content>
    </Container>
  );
});
